import { useRef, useSyncExternalStore, type CSSProperties, type DragEvent, type MouseEvent, type ReactNode } from 'react'
import { Bottone } from '../base.tsx'
import { IconaPiu } from '../Icone.tsx'
import {
  GIORNI_CORTI, giornoCorto, giornoInParole, giorniFra, lunediDi, meseInParole, minuti, oggiARoma, ora,
  periodoInParole, piuGiorni, piuMesi, primoDelMese, ultimoDelMese,
} from './date.ts'

/**
 * Calendario riusabile (docs/15-calendari-sedi.md, fasi 2 e 3): il calendario delle mediazioni, «Il mio
 * calendario» del mediatore e l'agenda della sede.
 *
 * Il componente disegna e basta: i dati li legge la pagina, un periodo alla volta (`periodoDellaVista`,
 * al piu' un mese, come chiede l'API) e li passa come eventi gia' decisi, con il titolo che chi guarda
 * puo' leggere. Niente qui sa che cosa e' una mediazione.
 *
 * Viste:
 *  - giorno: una griglia oraria, con una colonna per riga (stanza, mediatore, persona in turno) se ci sono
 *    righe, altrimenti una sola;
 *  - settimana, dal lunedi' al sabato: con le righe, una riga per risorsa e un riquadro per giorno; senza,
 *    la griglia oraria dei sei giorni. Le ore in cui la sede non e' aperta sono ombreggiate e chiusure e
 *    festivita' hanno la loro fascia grigia (gli stati della sede, GET /sedi/:id/stato);
 *  - mese: i giorni con i primi impegni, e il giorno si apre con un clic;
 *  - elenco stampabile, giorno per giorno.
 * Sotto i 600 px giorno e settimana diventano elenchi divisi per giorno, con ‹ › «Oggi» e «+» nella barra.
 *
 * Lo spazio vuoto e' un comando: `alVuoto` riceve giorno, ora e riga, per la creazione rapida.
 *
 * Gli impegni di tutto il giorno stanno in testa alla colonna (o in cima al riquadro), fuori dalla griglia
 * delle ore. Gli impegni `trascinabile` si spostano trascinandoli con il mouse (fase 3: turni e prenotazioni):
 * nella griglia delle ore cambiano ora e colonna, nella settimana a righe giorno e riga. Il calendario dice
 * dove sono stati lasciati (`trascina`); salvare e controllare tocca alla pagina. Dalla tastiera lo stesso
 * si fa dal dettaglio dell'impegno.
 */

export type Vista = 'giorno' | 'settimana' | 'mese' | 'elenco'

/** rosso: conflitto che va risolto; arancione: avviso; annullato e concluso: si vede che non vale piu'. */
export type TonoEvento = 'normale' | 'rosso' | 'arancione' | 'annullato' | 'concluso'

export type EventoCalendario = {
  chiave: string
  giorno: string
  dalle: string
  alle: string
  titolo: string
  dettaglio?: string
  /** La riga (risorsa o persona) a cui appartiene, nelle viste con le righe. */
  riga?: string
  /** Lo strato (mediazioni, appuntamenti, turni...): un segno nel bordo per riconoscerlo. */
  strato?: string
  tono?: TonoEvento
  /** Conflitti e avvisi, in breve: al passaggio del puntatore e nell'elenco. */
  avvisi?: string[]
  /** Impegno di cui si vede solo l'orario («Occupato (altra sede)»): non si apre. */
  occupato?: boolean
  /** Tutto il giorno: in testa alla colonna, senza ore. */
  tuttoIlGiorno?: boolean
  /**
   * In testa alla colonna con l'ora, fuori dalla griglia: per gli impegni lunghi che nella griglia della
   * settimana schiaccerebbero gli altri (i turni dell'agenda).
   */
  inTesta?: boolean
  /** Si sposta trascinandolo (fase 3: turni e prenotazioni, per chi li puo' modificare). */
  trascinabile?: boolean
  apri?: () => void
}

/** Dove e' stato lasciato un impegno trascinato: giorno, ora d'inizio, riga. */
export type PuntoTrascinato = { giorno: string; ora: string; riga?: string }

export type RigaCalendario = { chiave: string; titolo: string; sottotitolo?: string; gruppo?: string }

/** Un tratto di GET /sedi/:id/stato, con giorno e ore di Roma. */
export type TrattoSede = {
  giorno_dal: string; ora_dal: string; giorno_al: string; ora_al: string
  stato: 'aperta' | 'solo_interno' | 'fuori_orario' | 'chiusa' | 'festivita'; motivo: string | null; origine: string
}

export type PuntoVuoto = { giorno: string; ora: string; riga?: string }

// --- periodo ------------------------------------------------------------------------------------------

/** Il periodo da chiedere per una vista: il giorno, dal lunedi' al sabato, il mese. */
export function periodoDellaVista(vista: Vista, giorno: string): { dal: string; al: string } {
  if (vista === 'giorno') return { dal: giorno, al: giorno }
  if (vista === 'mese') return { dal: primoDelMese(giorno), al: ultimoDelMese(giorno) }
  const lunedi = lunediDi(giorno)
  return { dal: lunedi, al: piuGiorni(lunedi, 5) }
}

/** Avanti e indietro di un giorno, una settimana o un mese. */
export function spostaGiorno(vista: Vista, giorno: string, verso: -1 | 1): string {
  if (vista === 'giorno') return piuGiorni(giorno, verso)
  if (vista === 'mese') return piuMesi(giorno, verso)
  return piuGiorni(giorno, 7 * verso)
}

const titoloPeriodo = (vista: Vista, giorno: string) => {
  const { dal, al } = periodoDellaVista(vista, giorno)
  return vista === 'mese' ? meseInParole(giorno) : periodoInParole(dal, al)
}

/** Schermo stretto, come il telefono (sotto i 600 px): giorno e settimana diventano elenchi. */
export function useStretto() {
  const query = typeof window === 'undefined' ? null : window.matchMedia('(max-width: 600px)')
  return useSyncExternalStore(
    (avvisa) => { query?.addEventListener('change', avvisa); return () => query?.removeEventListener('change', avvisa) },
    () => query?.matches ?? false,
    () => false,
  )
}

// --- stati della sede -------------------------------------------------------------------------------

type Segmento = { da: number; a: number; stato: TrattoSede['stato']; motivo: string | null; origine: string }

/** I tratti della sede nel giorno, in minuti dalla mezzanotte. */
export function segmentiDelGiorno(tratti: TrattoSede[] | null | undefined, giorno: string): Segmento[] {
  if (!tratti) return []
  return tratti
    .filter((t) => t.giorno_dal <= giorno && (t.giorno_al > giorno || (t.giorno_al === giorno && t.ora_al !== '00:00')))
    .map((t) => ({
      da: t.giorno_dal < giorno ? 0 : minuti(t.ora_dal),
      a: t.giorno_al > giorno ? 1440 : minuti(t.ora_al),
      stato: t.stato, motivo: t.motivo, origine: t.origine,
    }))
    .filter((s) => s.a > s.da)
}

/** Chiusure e festivita' del giorno, per la fascia grigia: «Festa nazionale: Natale», «Chiusa 14:00-18:00: ...». */
export function fasciaDelGiorno(tratti: TrattoSede[] | null | undefined, giorno: string): string | null {
  const chiusi = segmentiDelGiorno(tratti, giorno).filter((s) => s.stato === 'chiusa' || s.stato === 'festivita')
  if (!chiusi.length) return null
  const [s] = chiusi
  const tipo = s.stato === 'festivita' ? 'Festa nazionale'
    : { festa_patronale: 'Festa patronale', ponte: 'Ponte' }[s.origine] ?? 'Chiusa'
  const ore = s.da === 0 && s.a === 1440 ? '' : ` ${ora(s.da)}-${ora(s.a)}`
  return `${tipo}${ore}${s.motivo ? `: ${s.motivo}` : ''}`
}

/** La sede non apre mai nel giorno (chiusura, festa, giorno senza fasce): il riquadro e' grigio. */
export function chiusoTuttoIlGiorno(tratti: TrattoSede[] | null | undefined, giorno: string): boolean {
  const segmenti = segmentiDelGiorno(tratti, giorno)
  return segmenti.length > 0 && !segmenti.some((s) => s.stato === 'aperta')
}

const apertoIn = (segmenti: Segmento[], da: number, a: number) =>
  !segmenti.length || segmenti.filter((s) => s.a > da && s.da < a).every((s) => s.stato === 'aperta')

/**
 * La prima ora libera della riga nel giorno, a mezz'ore, con la sede aperta se se ne conoscono gli
 * stati: e' l'ora che la creazione rapida propone quando lo spazio vuoto non dice l'ora.
 */
export function primaOraLibera(a: {
  giorno: string; riga?: string; eventi: EventoCalendario[]; tratti?: TrattoSede[] | null
  oraInizio: number; oraFine: number; durata?: number
}): string {
  const durata = a.durata ?? 60
  const segmenti = segmentiDelGiorno(a.tratti, a.giorno)
  const occupati = a.eventi.filter((e) => e.giorno === a.giorno && (!a.riga || e.riga === a.riga) && e.tono !== 'annullato'
    && !e.tuttoIlGiorno)
  for (let t = Math.max(a.oraInizio, 9) * 60; t + durata <= a.oraFine * 60; t += 30) {
    const libero = occupati.every((e) => minuti(e.alle) <= t || minuti(e.dalle) >= t + durata)
    if (libero && apertoIn(segmenti, t, t + durata)) return ora(t)
  }
  return '09:00'
}

// --- disposizione --------------------------------------------------------------------------------------

/** Impegni che si sovrappongono nella stessa colonna: una corsia ciascuno, affiancate. */
function corsie(eventi: EventoCalendario[]): Map<string, { corsia: number; corsie: number }> {
  const ordinati = [...eventi].sort((a, b) => a.dalle.localeCompare(b.dalle) || b.alle.localeCompare(a.alle))
  const esito = new Map<string, { corsia: number; corsie: number }>()
  let gruppo: EventoCalendario[] = []
  let fineGruppo = -1
  let fini: number[] = []
  const chiudi = () => { for (const e of gruppo) esito.get(e.chiave)!.corsie = fini.length; gruppo = []; fini = [] }
  for (const e of ordinati) {
    const da = minuti(e.dalle)
    if (da >= fineGruppo) chiudi()
    let corsia = fini.findIndex((f) => f <= da)
    if (corsia < 0) { corsia = fini.length; fini.push(0) }
    fini[corsia] = Math.max(minuti(e.alle), da + 15)
    esito.set(e.chiave, { corsia, corsie: 1 })
    gruppo.push(e)
    fineGruppo = Math.max(fineGruppo, fini[corsia])
  }
  chiudi()
  return esito
}

/** Geometria come variabili CSS: il foglio di stile fa il resto. */
const geometria = (v: Record<string, number>) =>
  Object.fromEntries(Object.entries(v).map(([k, x]) => [`--${k}`, String(x)])) as CSSProperties

const orario = (e: EventoCalendario) => (e.tuttoIlGiorno ? 'tutto il giorno' : `${e.dalle}-${e.alle}`)

const testoCompleto = (e: EventoCalendario) =>
  [orario(e), e.titolo, e.dettaglio, ...(e.avvisi ?? []), e.trascinabile ? 'si sposta trascinandolo' : null]
    .filter(Boolean).join(' · ')

/** Il trascinamento in corso: quale impegno e a quanti minuti dal suo inizio lo si e' preso. */
type Presa = { evento: EventoCalendario; scarto: number }

/**
 * Un impegno: si apre con un clic, salvo gli impegni di altri. `compatto`: nel flusso di un riquadro, con
 * l'ora d'inizio e il titolo su una riga; `conDettaglio` aggiunge sotto il dettaglio (la settimana a righe).
 * Con `prendi` e l'impegno trascinabile, si trascina.
 */
function Blocco({ e, stile, compatto = false, conDettaglio = !compatto, prendi }: {
  e: EventoCalendario; stile?: CSSProperties; compatto?: boolean; conDettaglio?: boolean
  prendi?: (e: EventoCalendario, ev: DragEvent<HTMLElement>) => void
}) {
  const classi = ['calendario-blocco', `calendario-tono-${e.tono ?? 'normale'}`]
  if (e.occupato) classi.push('calendario-occupato')
  if (compatto) classi.push('calendario-blocco-compatto')
  if (e.avvisi?.length) classi.push('calendario-con-avvisi')
  if (e.strato) classi.push(`calendario-strato-${e.strato}`)
  const trascina = !!prendi && !!e.trascinabile && !e.occupato
  if (trascina) classi.push('calendario-trascinabile')
  const presa = trascina ? {
    draggable: true,
    onDragStart: (ev: DragEvent<HTMLElement>) => {
      ev.dataTransfer.effectAllowed = 'move'
      ev.dataTransfer.setData('text/plain', e.chiave)
      prendi!(e, ev)
    },
  } : {}
  const corpo = (
    <>
      <span className="calendario-blocco-testa">
        <span className="calendario-blocco-ora">{e.tuttoIlGiorno ? '' : e.dalle}{compatto || e.tuttoIlGiorno ? '' : `-${e.alle}`}</span>
        <span className="calendario-blocco-titolo">{e.titolo}</span>
        {!!e.avvisi?.length && <span className="calendario-blocco-segno" aria-hidden="true">!</span>}
      </span>
      {e.dettaglio && conDettaglio && <span className="calendario-blocco-dettaglio">{e.dettaglio}</span>}
    </>
  )
  if (e.occupato || !e.apri) {
    return <span className={classi.join(' ')} style={stile} title={testoCompleto(e)} {...presa}>{corpo}</span>
  }
  return (
    <button type="button" className={classi.join(' ')} style={stile} title={testoCompleto(e)}
            aria-label={testoCompleto(e)} onClick={e.apri} {...presa}>
      {corpo}
    </button>
  )
}

// --- griglia oraria -------------------------------------------------------------------------------------

type Colonna = { chiave: string; titolo: ReactNode; sottotitolo?: string; giorno: string; riga?: string; fascia?: string | null }

/** Colonne con le ore in verticale: il giorno (una colonna per riga) o la settimana senza righe. */
function GrigliaOre({ colonne, eventi, statoSede, oraInizio, oraFine, alVuoto, etichettaVuoto, trascina }: {
  colonne: Colonna[]; eventi: EventoCalendario[]; statoSede?: TrattoSede[] | null
  oraInizio: number; oraFine: number; alVuoto?: (p: PuntoVuoto) => void; etichettaVuoto: string
  trascina?: (e: EventoCalendario, a: PuntoTrascinato) => void
}) {
  const inizio = oraInizio * 60
  const ampiezza = (oraFine - oraInizio) * 60
  const ore = Array.from({ length: oraFine - oraInizio }, (_, i) => oraInizio + i)
  const percento = (min: number) => ((Math.min(Math.max(min, inizio), inizio + ampiezza) - inizio) / ampiezza) * 100
  const presa = useRef<Presa | null>(null)
  const prendi = trascina ? (e: EventoCalendario, ev: DragEvent<HTMLElement>) => {
    const r = ev.currentTarget.getBoundingClientRect()
    const durata = minuti(e.alle) - minuti(e.dalle)
    presa.current = { evento: e, scarto: Math.round(((ev.clientY - r.top) / Math.max(r.height, 1)) * durata / 15) * 15 }
  } : undefined
  const lascia = (c: Colonna) => (ev: DragEvent<HTMLDivElement>) => {
    const p = presa.current
    presa.current = null
    if (!p || !trascina) return
    ev.preventDefault()
    const r = ev.currentTarget.getBoundingClientRect()
    const durata = minuti(p.evento.alle) - minuti(p.evento.dalle)
    const min = inizio + Math.round(((ev.clientY - r.top) / r.height) * ampiezza / 15) * 15 - p.scarto
    const ora0 = Math.min(Math.max(min, 0), 1440 - durata)
    if (c.giorno === p.evento.giorno && ora(ora0) === p.evento.dalle && (c.riga ?? p.evento.riga) === p.evento.riga) return
    trascina(p.evento, { giorno: c.giorno, ora: ora(ora0), riga: c.riga })
  }

  const vuoto = (c: Colonna) => (ev: MouseEvent<HTMLButtonElement>) => {
    if (!alVuoto) return
    let punto: string
    if (ev.detail === 0) {
      // Dalla tastiera non c'e' un punto: la prima ora libera.
      punto = primaOraLibera({ giorno: c.giorno, riga: c.riga, eventi, tratti: statoSede, oraInizio, oraFine })
    } else {
      const r = ev.currentTarget.getBoundingClientRect()
      const min = inizio + Math.floor(((ev.clientY - r.top) / r.height) * ampiezza / 15) * 15
      punto = ora(Math.min(Math.max(min, inizio), inizio + ampiezza - 15))
    }
    alVuoto({ giorno: c.giorno, ora: punto, riga: c.riga })
  }

  return (
    <div className="calendario-griglia" style={geometria({ colonne: colonne.length, ore: ore.length })}>
      <div className="calendario-griglia-capo">
        <span />
        {colonne.map((c) => (
          <div key={c.chiave} className="calendario-colonna-capo">
            <span className="calendario-colonna-titolo">{c.titolo}</span>
            {c.sottotitolo && <span className="calendario-colonna-sottotitolo">{c.sottotitolo}</span>}
            {c.fascia && <span className="calendario-fascia">{c.fascia}</span>}
            {eventi.filter((e) => (e.tuttoIlGiorno || e.inTesta) && e.giorno === c.giorno && (c.riga === undefined || e.riga === c.riga))
              .sort((a, b) => Number(!!b.tuttoIlGiorno) - Number(!!a.tuttoIlGiorno) || a.dalle.localeCompare(b.dalle))
              .map((e) => <Blocco key={e.chiave} e={e} compatto conDettaglio={false} />)}
          </div>
        ))}
      </div>
      <div className="calendario-griglia-corpo">
        <div className="calendario-ore" aria-hidden="true">
          {ore.map((h) => <span key={h}>{ora(h * 60)}</span>)}
        </div>
        {colonne.map((c) => {
          const suoi = eventi.filter((e) => e.giorno === c.giorno && (c.riga === undefined || e.riga === c.riga)
            && !e.tuttoIlGiorno && !e.inTesta)
          const posti = corsie(suoi)
          return (
            <div key={c.chiave} className="calendario-colonna"
                 onDragOver={trascina ? (ev) => { if (presa.current) ev.preventDefault() } : undefined}
                 onDrop={trascina ? lascia(c) : undefined}>
              {segmentiDelGiorno(statoSede, c.giorno).filter((s) => s.stato !== 'aperta').map((s, i) => (
                <span key={i} className={`calendario-chiuso calendario-chiuso-${s.stato}`}
                      style={geometria({ da: percento(s.da), durata: percento(s.a) - percento(s.da) })}
                      title={s.motivo ?? undefined} />
              ))}
              {alVuoto && (
                <button type="button" className="calendario-vuoto" onClick={vuoto(c)}
                        aria-label={`${etichettaVuoto}: ${giornoInParole(c.giorno)}${typeof c.titolo === 'string' && c.riga ? `, ${c.titolo}` : ''}`} />
              )}
              {suoi.map((e) => {
                const p = posti.get(e.chiave)!
                return (
                  <Blocco key={e.chiave} e={e} prendi={prendi} stile={geometria({
                    da: percento(minuti(e.dalle)),
                    durata: Math.max(percento(minuti(e.alle)) - percento(minuti(e.dalle)), 2.5),
                    corsia: p.corsia, corsie: p.corsie,
                  })} />
                )
              })}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// --- settimana con le righe -------------------------------------------------------------------------------

/** Una riga per risorsa, un riquadro per giorno: gli impegni in ordine di ora, lo spazio vuoto per crearne. */
function SettimanaARighe({ giorni, righe, eventi, statoSede, oraInizio, oraFine, alVuoto, etichettaVuoto, etichettaRighe, trascina }: {
  giorni: string[]; righe: RigaCalendario[]; eventi: EventoCalendario[]; statoSede?: TrattoSede[] | null
  oraInizio: number; oraFine: number; alVuoto?: (p: PuntoVuoto) => void; etichettaVuoto: string; etichettaRighe: string
  trascina?: (e: EventoCalendario, a: PuntoTrascinato) => void
}) {
  const oggi = oggiARoma()
  // Nella settimana a righe si cambia giorno e riga; l'ora resta.
  const presa = useRef<EventoCalendario | null>(null)
  const prendi = trascina ? (e: EventoCalendario) => { presa.current = e } : undefined
  const lascia = (giorno: string, riga: string) => (ev: DragEvent<HTMLDivElement>) => {
    const e = presa.current
    presa.current = null
    if (!e || !trascina) return
    ev.preventDefault()
    if (giorno === e.giorno && riga === e.riga) return
    trascina(e, { giorno, ora: e.dalle, riga })
  }
  let gruppo: string | undefined
  return (
    <div className="calendario-righe" role="table" aria-label={`Settimana per ${etichettaRighe}`}
         style={geometria({ giorni: giorni.length })}>
      <div className="calendario-righe-capo" role="row">
        <span role="columnheader" className="calendario-righe-nome">{etichettaRighe}</span>
        {giorni.map((g) => {
          const fascia = fasciaDelGiorno(statoSede, g)
          return (
            <span key={g} role="columnheader" className={`calendario-giorno-capo${g === oggi ? ' calendario-oggi' : ''}`}>
              {giornoCorto(g)}
              {fascia && <span className="calendario-fascia">{fascia}</span>}
            </span>
          )
        })}
      </div>
      {righe.map((r) => {
        const capoGruppo = r.gruppo && r.gruppo !== gruppo ? r.gruppo : null
        gruppo = r.gruppo
        return (
          <div key={r.chiave} className="calendario-righe-blocco" role="rowgroup">
            {capoGruppo && <div className="calendario-gruppo" role="row"><span role="cell">{capoGruppo}</span></div>}
            <div className="calendario-righe-riga" role="row">
              <span role="rowheader" className="calendario-righe-nome">
                <span className="calendario-righe-titolo">{r.titolo}</span>
                {r.sottotitolo && <span className="calendario-righe-sottotitolo">{r.sottotitolo}</span>}
              </span>
              {giorni.map((g) => {
                const suoi = eventi.filter((e) => e.giorno === g && e.riga === r.chiave)
                  .sort((a, b) => Number(!!b.tuttoIlGiorno) - Number(!!a.tuttoIlGiorno) || a.dalle.localeCompare(b.dalle))
                const chiuso = chiusoTuttoIlGiorno(statoSede, g)
                return (
                  <div key={g} role="cell" className={`calendario-cella${chiuso ? ' calendario-cella-chiusa' : ''}`}
                       onDragOver={trascina ? (ev) => { if (presa.current) ev.preventDefault() } : undefined}
                       onDrop={trascina ? lascia(g, r.chiave) : undefined}>
                    {alVuoto && (
                      <button type="button" className="calendario-vuoto"
                              aria-label={`${etichettaVuoto}: ${giornoInParole(g)}, ${r.titolo}`}
                              onClick={() => alVuoto({
                                giorno: g, riga: r.chiave,
                                ora: primaOraLibera({ giorno: g, riga: r.chiave, eventi, tratti: statoSede, oraInizio, oraFine }),
                              })} />
                    )}
                    {suoi.map((e) => <Blocco key={e.chiave} e={e} compatto conDettaglio prendi={prendi} />)}
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// --- mese ------------------------------------------------------------------------------------------------

function Mese({ giorno, eventi, statoSede, apriGiorno }: {
  giorno: string; eventi: EventoCalendario[]; statoSede?: TrattoSede[] | null; apriGiorno?: (g: string) => void
}) {
  const primo = primoDelMese(giorno)
  const ultimo = ultimoDelMese(giorno)
  const giorni = giorniFra(lunediDi(primo), piuGiorni(lunediDi(ultimo), 6))
  const oggi = oggiARoma()
  return (
    <div className="calendario-mese" role="table" aria-label={meseInParole(giorno)}>
      <div className="calendario-mese-capo" role="row">
        {GIORNI_CORTI.map((g) => <span key={g} role="columnheader">{g}</span>)}
      </div>
      <div className="calendario-mese-corpo">
        {giorni.map((g) => {
          const fuori = g < primo || g > ultimo
          const suoi = fuori ? [] : eventi.filter((e) => e.giorno === g)
            .sort((a, b) => Number(!!b.tuttoIlGiorno) - Number(!!a.tuttoIlGiorno) || a.dalle.localeCompare(b.dalle))
          const fascia = fuori ? null : fasciaDelGiorno(statoSede, g)
          const classi = ['calendario-mese-giorno']
          if (fuori) classi.push('calendario-fuori-mese')
          if (g === oggi) classi.push('calendario-oggi')
          if (!fuori && chiusoTuttoIlGiorno(statoSede, g)) classi.push('calendario-cella-chiusa')
          return (
            <div key={g} role="cell" className={classi.join(' ')}>
              {apriGiorno && !fuori
                ? <button type="button" className="calendario-mese-numero" onClick={() => apriGiorno(g)}
                          aria-label={`Apri ${giornoInParole(g)}`}>{Number(g.slice(8, 10))}</button>
                : <span className="calendario-mese-numero">{Number(g.slice(8, 10))}</span>}
              {fascia && <span className="calendario-fascia">{fascia}</span>}
              {suoi.slice(0, 3).map((e) => <Blocco key={e.chiave} e={e} compatto />)}
              {suoi.length > 3 && (
                apriGiorno
                  ? <button type="button" className="calendario-altri" onClick={() => apriGiorno(g)}>e altri {suoi.length - 3}</button>
                  : <span className="calendario-altri">e altri {suoi.length - 3}</span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// --- elenco ----------------------------------------------------------------------------------------------

/** Giorno per giorno: la vista stampabile e, sotto i 600 px, la vista del telefono. */
function Elenco({ giorni, eventi, statoSede, righe, vuoto, alVuoto, etichettaVuoto, oraInizio, oraFine }: {
  giorni: string[]; eventi: EventoCalendario[]; statoSede?: TrattoSede[] | null; righe?: RigaCalendario[]
  vuoto: string; alVuoto?: (p: PuntoVuoto) => void; etichettaVuoto: string; oraInizio: number; oraFine: number
}) {
  const nomeRiga = new Map((righe ?? []).map((r) => [r.chiave, r.titolo]))
  return (
    <div className="calendario-elenco">
      {giorni.map((g) => {
        const suoi = eventi.filter((e) => e.giorno === g)
          .sort((a, b) => Number(!!b.tuttoIlGiorno) - Number(!!a.tuttoIlGiorno) || a.dalle.localeCompare(b.dalle))
        const fascia = fasciaDelGiorno(statoSede, g)
        return (
          <section key={g} className="calendario-elenco-giorno">
            <h3 className="calendario-elenco-titolo">
              <span>{giornoInParole(g)}</span>
              {alVuoto && (
                <Bottone variante="discreto" dimensione="piccolo" icona={<IconaPiu />}
                         aria-label={`${etichettaVuoto}: ${giornoInParole(g)}`}
                         onClick={() => alVuoto({ giorno: g, ora: primaOraLibera({ giorno: g, eventi, tratti: statoSede, oraInizio, oraFine }) })} />
              )}
            </h3>
            {fascia && <p className="calendario-fascia">{fascia}</p>}
            {suoi.length
              ? (
                <ul className="calendario-elenco-voci">
                  {suoi.map((e) => (
                    <li key={e.chiave} className={`calendario-elenco-voce calendario-tono-${e.tono ?? 'normale'}${e.occupato ? ' calendario-occupato' : ''}`}>
                      <span className="calendario-elenco-ora">{e.tuttoIlGiorno ? 'tutto il giorno' : `${e.dalle}-${e.alle}`}</span>
                      <span className="calendario-elenco-testo">
                        {e.apri && !e.occupato
                          ? <button type="button" className="calendario-elenco-apri" onClick={e.apri}>{e.titolo}</button>
                          : <span className="calendario-elenco-apri">{e.titolo}</span>}
                        <span className="calendario-elenco-dettaglio">
                          {[e.riga && nomeRiga.get(e.riga), e.dettaglio].filter(Boolean).join(' · ')}
                        </span>
                        {e.avvisi?.map((a) => <span key={a} className="calendario-elenco-avviso">{a}</span>)}
                      </span>
                    </li>
                  ))}
                </ul>
              )
              : <p className="calendario-elenco-vuoto">{vuoto}</p>}
          </section>
        )
      })}
    </div>
  )
}

// --- barra e calendario ----------------------------------------------------------------------------------

const NOMI_VISTE: Record<Vista, string> = { giorno: 'Giorno', settimana: 'Settimana', mese: 'Mese', elenco: 'Elenco' }

/** ‹ «Oggi» › e il periodo, le viste, la stampa dell'elenco, il «+» della creazione. */
export function BarraCalendario({ vista, cambiaVista, giorno, cambiaGiorno, viste = ['giorno', 'settimana', 'mese', 'elenco'],
  nuovo, etichettaNuovo = 'Nuovo', children }: {
  vista: Vista; cambiaVista: (v: Vista) => void; giorno: string; cambiaGiorno: (g: string) => void
  viste?: Vista[]; nuovo?: () => void; etichettaNuovo?: string; children?: ReactNode
}) {
  const passo = { giorno: 'giorno', settimana: 'settimana', mese: 'mese', elenco: 'settimana' }[vista]
  return (
    <div className="calendario-barra">
      <div className="calendario-barra-periodo">
        <Bottone variante="secondario" dimensione="piccolo" aria-label={`${passo === 'settimana' ? 'Settimana precedente' : passo === 'mese' ? 'Mese precedente' : 'Giorno precedente'}`}
                 onClick={() => cambiaGiorno(spostaGiorno(vista, giorno, -1))}>‹</Bottone>
        <Bottone variante="secondario" dimensione="piccolo" onClick={() => cambiaGiorno(oggiARoma())}>Oggi</Bottone>
        <Bottone variante="secondario" dimensione="piccolo" aria-label={`${passo === 'settimana' ? 'Settimana successiva' : passo === 'mese' ? 'Mese successivo' : 'Giorno successivo'}`}
                 onClick={() => cambiaGiorno(spostaGiorno(vista, giorno, 1))}>›</Bottone>
        <h2 className="calendario-barra-titolo" aria-live="polite">{titoloPeriodo(vista, giorno)}</h2>
      </div>
      <div className="calendario-barra-comandi">
        <div className="calendario-viste" role="group" aria-label="Vista">
          {viste.map((v) => (
            <Bottone key={v} variante={v === vista ? 'primario' : 'discreto'} dimensione="piccolo"
                     aria-pressed={v === vista} onClick={() => cambiaVista(v)}>{NOMI_VISTE[v]}</Bottone>
          ))}
        </div>
        {vista === 'elenco' && <Bottone variante="secondario" dimensione="piccolo" onClick={() => window.print()}>Stampa</Bottone>}
        {children}
        {nuovo && (
          <Bottone variante="primario" dimensione="piccolo" icona={<IconaPiu />} onClick={nuovo} aria-label={etichettaNuovo}>
            <span className="calendario-solo-ampio">{etichettaNuovo}</span>
          </Bottone>
        )}
      </div>
    </div>
  )
}

export function Calendario({
  vista, giorno, eventi, righe, statoSede, alVuoto, apriGiorno, etichettaVuoto = 'Nuovo impegno',
  etichettaRighe = 'Righe', vuoto = 'Niente in questo giorno.', oraInizio = 8, oraFine = 19, trascina,
}: {
  vista: Vista
  /** Un giorno qualsiasi del periodo: la vista calcola il resto. */
  giorno: string
  eventi: EventoCalendario[]
  /** Righe per risorsa o persona: colonne nel giorno, righe nella settimana. */
  righe?: RigaCalendario[]
  /** Stati della sede per ombreggiare e fasciare; senza, niente ombreggiatura. */
  statoSede?: TrattoSede[] | null
  alVuoto?: (p: PuntoVuoto) => void
  apriGiorno?: (giorno: string) => void
  etichettaVuoto?: string
  etichettaRighe?: string
  vuoto?: string
  oraInizio?: number
  oraFine?: number
  /** Un impegno trascinabile lasciato altrove: la pagina salva e ricarica. */
  trascina?: (e: EventoCalendario, a: PuntoTrascinato) => void
}) {
  const stretto = useStretto()
  const { dal, al } = periodoDellaVista(vista, giorno)
  const giorni = giorniFra(dal, al)
  // Le ore della griglia si allargano per mostrare gli impegni fuori dalla fascia di sempre.
  const conOre = eventi.filter((e) => e.giorno >= dal && e.giorno <= al && !e.tuttoIlGiorno && !e.inTesta)
  const primaOra = Math.min(oraInizio, ...conOre.map((e) => Math.floor(minuti(e.dalle) / 60)))
  const ultimaOra = Math.max(oraFine, ...conOre.map((e) => Math.min(24, Math.ceil(minuti(e.alle === '00:00' ? '24:00' : e.alle) / 60))))
  // Sul telefono non si trascina: si apre l'impegno e si cambia dal modulo.
  const comune = { eventi, statoSede, oraInizio: primaOra, oraFine: ultimaOra, alVuoto, etichettaVuoto, trascina: stretto ? undefined : trascina }

  if (vista === 'elenco' || (stretto && vista !== 'mese')) {
    const { trascina: _t, ...perElenco } = comune
    return <Elenco giorni={giorni} righe={righe} vuoto={vuoto} {...perElenco} />
  }
  if (vista === 'mese') return <Mese giorno={giorno} eventi={eventi} statoSede={statoSede} apriGiorno={apriGiorno} />
  if (vista === 'settimana' && righe) {
    return <SettimanaARighe giorni={giorni} righe={righe} etichettaRighe={etichettaRighe} {...comune} />
  }
  const colonne: Colonna[] = vista === 'giorno'
    ? (righe?.length
      ? righe.map((r) => ({ chiave: r.chiave, titolo: r.titolo, sottotitolo: r.sottotitolo, giorno, riga: r.chiave }))
      : [{ chiave: giorno, titolo: giornoInParole(giorno), giorno, fascia: fasciaDelGiorno(statoSede, giorno) }])
    : giorni.map((g) => ({ chiave: g, titolo: giornoCorto(g), giorno: g, fascia: fasciaDelGiorno(statoSede, g) }))
  return (
    <>
      {vista === 'giorno' && righe?.length ? (
        fasciaDelGiorno(statoSede, giorno) && <p className="calendario-fascia">{fasciaDelGiorno(statoSede, giorno)}</p>
      ) : null}
      <GrigliaOre colonne={colonne} {...comune} />
    </>
  )
}
